import { useFormContext } from '@calendly/form'
import { compact } from 'lodash-es'
import { nanoid } from 'nanoid'
import { useCallback, useMemo } from 'react'

import { useETEditorFormikPoolingType } from 'services/event_types'

import { getCurrentOrganizer } from '../../utils'
import {
  filterActiveLocations,
  getIsRROrCRRWithoutCollectivePools,
  getMembershipKey,
  getUserPools,
  normalizeLocationConfigurationsOrdering,
  sortLocationsByPosition
} from '../utils'


const initialLocationOptions = {
  location: null,
  additional_info: null,
  phone_number: null,
  hide_location: false,
  additionalInfo: '',
  hideLocation: 'visible'
}

export const useLocationManagement = (eventType, membership, location) => {
  const { values, setFieldValue } = useFormContext()
  const { locationConfigurations, memberships } = values
  const { isRoundRobin, isCollectiveRoundRobin } = useETEditorFormikPoolingType()
  const userPools = getUserPools(values)
  const isRoundRobinOrCollectiveRoundRobin = getIsRROrCRRWithoutCollectivePools(isRoundRobin, isCollectiveRoundRobin, userPools)

  const membershipIndex = useMemo(() => (
    memberships && memberships.findIndex((m) => getMembershipKey(m) === getMembershipKey(membership))
  ), [memberships, membership])

  const fieldName = useMemo(() => (isRoundRobinOrCollectiveRoundRobin
    ? `memberships[${membershipIndex}].locationConfiguration` : 'locationConfigurations'),
  [isRoundRobinOrCollectiveRoundRobin, membershipIndex])

  const locations = useMemo(() => (isRoundRobinOrCollectiveRoundRobin
    ? compact([membership?.locationConfiguration]) : locationConfigurations),
  [isRoundRobinOrCollectiveRoundRobin, locationConfigurations, membership?.locationConfiguration])
  const filteredLocations = useMemo(() => filterActiveLocations(locations), [locations])
  const sortedLocations = useMemo(() => sortLocationsByPosition(filteredLocations), [filteredLocations])
  const locationId = useMemo(() => location?.id || location?.nanoId, [location])

  const fieldNamePrefix = useMemo(() => {
    if (!location) return ''
    const locationIndex = filteredLocations.findIndex((lc) => (lc.id || lc.nanoId) === locationId)
    return isRoundRobinOrCollectiveRoundRobin
      ? `memberships[${membershipIndex}].locationConfiguration` : `locationConfigurations[${locationIndex}]`
  }, [filteredLocations, isRoundRobinOrCollectiveRoundRobin, location, locationId, membershipIndex])

  const addLocation = useCallback((kind, setOrganizer = false) => {
    let newLocation = {
      ...initialLocationOptions,
      kind,
      nanoId: nanoid(5),
      position: locations.length
    }

    if (setOrganizer) {
      const organizer = getCurrentOrganizer(memberships, kind, eventType.expected_calendar_pusher_membership_id)
      newLocation = { ...newLocation, organizer_user_id: organizer?.user_id ?? null }
    }

    if (isRoundRobinOrCollectiveRoundRobin) {
      setFieldValue(fieldName, newLocation)
    } else {
      const newLocations = [...locations, newLocation]
      const normalizedLocations = normalizeLocationConfigurationsOrdering(newLocations)
      setFieldValue(fieldName, normalizedLocations)
    }
  }, [locations,
    isRoundRobinOrCollectiveRoundRobin,
    memberships,
    eventType.expected_calendar_pusher_membership_id,
    setFieldValue,
    fieldName])

  const removeLocation = useCallback(() => {
    const updatedLocationConfigurations = normalizeLocationConfigurationsOrdering(locations
      .map((config) => (config.id === locationId ? { ...config, _destroy: true } : config)
      )
      .filter((config) => !(config.nanoId === locationId)
      ))

    if (isRoundRobinOrCollectiveRoundRobin) {
      setFieldValue(fieldName, updatedLocationConfigurations[0])
    } else {
      setFieldValue(fieldName, updatedLocationConfigurations)
    }
  }, [locations, isRoundRobinOrCollectiveRoundRobin, setFieldValue, fieldName, locationId])


  return { addLocation, removeLocation, allLocations: locations, filteredLocations, sortedLocations, fieldNamePrefix }
}
